import React from 'react';

import { PopperSubmenuContainer } from '../../Components/PopperSubmenuContainer';
import { delayedMobileCloseClickHandler } from '../../Components/utils';
import { useContextSwitcherDialogController } from '../../ContextState/useContextSwitcherDialogController';

import { IContextSwitcherData, IEntityDetails } from '../../types';

import { AppSwitcherSelectionDialog } from '../AppSwitcher/AppSwitcherSelectionDialog';
import { ContextSwitcher } from '../ContextSwitcher';
import { Error } from '../ErrorState';

import { EntitySwitcherDialog } from './EntitySwitcherDialog';

interface IDesktopContextSwitcherProps {
  contextSwitcherData: IContextSwitcherData;
  showContextSwitcher: boolean;
  anchorEl: HTMLDivElement;
  firstContextSwitcherItemRef?: React.MutableRefObject<HTMLLIElement>;
  loadingError?: boolean;
  closeSubmenu(): void;
}
export const DesktopContextSwitcher: React.FC<IDesktopContextSwitcherProps> = ({
  contextSwitcherData,
  showContextSwitcher,
  anchorEl,
  firstContextSwitcherItemRef,
  loadingError,
  closeSubmenu
}) => {
  const {
    selectedEntity,
    setSelectedEntity,
    showEntitySelectionDialog,
    setShowEntitySelectionDialog,
    showAppSelectionDialog,
    setShowAppSelectionDialog
  } = useContextSwitcherDialogController();

  const { entitledEntities, activeEntity, activeProductKey, forcedDefaultApplication, onEntitySelect } =
    contextSwitcherData || {};

  return (
    <>
      <PopperSubmenuContainer
        disablePortal={true}
        open={showContextSwitcher ?? false}
        anchorEl={anchorEl}
        dataTest={null}
        placement="bottom-start"
        handleClose={() => delayedMobileCloseClickHandler(closeSubmenu)}
        positionOffset={{
          y: 3
        }}
      >
        {loadingError && <Error />}
        {!loadingError && (
          <ContextSwitcher
            firstContextSwitcherItemRef={firstContextSwitcherItemRef}
            anchorEl={anchorEl}
            entitledEntities={entitledEntities}
            activeEntity={activeEntity}
            activeProductKey={activeProductKey}
            forcedDefaultApplication={forcedDefaultApplication}
            onEntitySelect={onEntitySelect}
            handleEntitySelect={(entity: IEntityDetails) => {
              setSelectedEntity(entity);
            }}
            openEntitySelectionDialog={() => {
              setShowEntitySelectionDialog(true);
              closeSubmenu();
            }}
            openAppSelectionDialog={() => {
              setShowAppSelectionDialog(true);
              closeSubmenu();
            }}
            handleClose={closeSubmenu}
          />
        )}
      </PopperSubmenuContainer>
      {/* Nesting these dialogs inside of a Popper component causes unwanted behavior with
        the entity selection's AutoComplete component.
        Placing them here allows the dialogs to be rendered outside of the Popper component and avoid those issues.
      */}
      <EntitySwitcherDialog
        open={showEntitySelectionDialog}
        activeEntity={activeEntity}
        selectedEntity={selectedEntity}
        entitledEntities={entitledEntities}
        handleEntitySelect={(entity: IEntityDetails | null) => {
          setSelectedEntity(entity);
        }}
        handleClose={() => {
          setShowEntitySelectionDialog(false);
        }}
        openAppSelectionDialog={() => setShowAppSelectionDialog(true)}
        forcedDefaultApplication={forcedDefaultApplication}
        onEntitySelect={onEntitySelect}
      />
      <AppSwitcherSelectionDialog
        open={showAppSelectionDialog}
        selectedEntity={selectedEntity}
        onEntitySelect={onEntitySelect}
        handleClose={() => {
          setShowAppSelectionDialog(false);
          // The MUI dialog has a close animation,
          // so we give it time to finish before resetting the selected entity
          setTimeout(() => {
            setSelectedEntity(null);
          }, 500);
        }}
      />
    </>
  );
};
